import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckIcon from '@mui/icons-material/Check';
import { CustomDatetimepicker } from '@components';
import { RootState } from '@stores/store';
import { useUpdatePayoutStatusMutation } from '@api/commissions';
import { PayoutStatus as PayoutStatusEnum, TOAST_TYPES } from '@constants/constants';
import { setToastMessage } from '@stores/App/slice';

const PENDING_GRAPHICS_COLOR = '#2F736E1F';
const PENDING_TEXT_COLOR = '#17191C59';
const INITIATED_TEXT_COLOR = '#51B8B0';
const INITIATED_GRAPHICS_COLOR = '#2F736E';

interface PaymentStatusProps {
  paymentId: string;
  paymentStatus: PayoutStatusEnum;
  paymentDate?: string;
  transactionId?: string;
  description?: string;
  approvedDate?: string;
  handlePayoutStatusChange: () => void;
}

const labelStyle = {
  fontSize: '0.875rem',
  lineHeight: '1.125rem',
  fontWeight: 700,
  color: '#6C6C6C',
  marginBottom: '0.5rem',
};

const textStyle = {
  fontSize: '1rem',
  lineHeight: '1.25rem',
  fontWeight: 700,
  color: '#162936',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const paymentStatusStyle = {
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  fontWeight: 700,
  color: PENDING_TEXT_COLOR,
};

const PaymentStatus: FC<PaymentStatusProps> = ({
  paymentId,
  paymentStatus,
  paymentDate,
  transactionId,
  description,
  handlePayoutStatusChange,
  approvedDate,
}) => {
  const userRole = useSelector((state: RootState) => state.UserData.user.role);

  const dispatch = useDispatch();

  const [updatePayoutStatus, { isLoading: isUpdatingPayoutStatus }] =
    useUpdatePayoutStatusMutation();

  const [form, setForm] = useState<{
    paymentDate: Dayjs | null;
    transactionId: string;
    description: string;
  }>({
    paymentDate: null,
    transactionId: '',
    description: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState<{
    paymentDate: boolean;
    transactionId: boolean;
  }>({
    paymentDate: false,
    transactionId: false,
  });

  const [errors, setErrors] = useState<{
    paymentDate: string | null;
    transactionId: string | null;
  }>({
    paymentDate: 'Payment date is required',
    transactionId: 'Transaction ID is required',
  });

  const isAdmin = userRole === 'ADMIN';
  const isPaymentCompleted = paymentStatus === PayoutStatusEnum.PAID;
  const isPaymentInitiated = paymentStatus === PayoutStatusEnum.INITIATED;
  const isPaymentPending = paymentStatus === PayoutStatusEnum.PENDING;
  const showPaymentForm = isPaymentInitiated && isAdmin;
  const isFormValid = !errors.paymentDate && !errors.transactionId;

  const checkFormErrors = (newFormValue: typeof form) => {
    const newErrors: Record<keyof typeof errors, string | null> = {
      paymentDate: null,
      transactionId: null,
    };

    if (!newFormValue.paymentDate) {
      newErrors.paymentDate = 'Payment date is required';
    }
    if (!newFormValue.transactionId) {
      newErrors.transactionId = 'Transaction ID is required';
    }

    setErrors(newErrors);
  };

  const handleFormChange = (key: keyof typeof form, value: string | Dayjs | null) => {
    const newFormValue = { ...form, [key]: value };
    setForm(newFormValue);
    checkFormErrors(newFormValue);
  };

  const handleBlurOnForm = (key: keyof typeof form) => {
    setTouched({ ...touched, [key]: true });
  };

  const handleResetPayment = async () => {
    setForm({
      paymentDate: null,
      transactionId: '',
      description: '',
    });
    setTouched({
      paymentDate: false,
      transactionId: false,
    });
    setSubmitted(false);
    checkFormErrors(form);
  };

  const handleApprovePayment = async () => {
    try {
      await updatePayoutStatus({
        id: paymentId,
        payload: {
          toStatus: PayoutStatusEnum.INITIATED,
        },
      }).unwrap();

      dispatch(
        setToastMessage({
          message: 'Payment approved successfully',
          type: TOAST_TYPES.SUCCESS,
        }),
      );

      handlePayoutStatusChange();
    } catch (error: any) {
      dispatch(
        setToastMessage({
          message: error?.error?.message || 'Failed to approve payment',
          type: TOAST_TYPES.ERROR,
        }),
      );
    }
  };

  const handleRecordPayment = async () => {
    setSubmitted(true);
    if (!isFormValid) return;

    try {
      const payload = {
        toStatus: PayoutStatusEnum.PAID,
        payoutDate: form.paymentDate?.toISOString(),
        transactionId: form.transactionId,
        description: form.description,
      };

      await updatePayoutStatus({
        id: paymentId,
        payload,
      }).unwrap();

      dispatch(
        setToastMessage({
          message: 'Payment recorded successfully',
          type: TOAST_TYPES.SUCCESS,
        }),
      );

      handlePayoutStatusChange();
    } catch (error: any) {
      dispatch(
        setToastMessage({
          message: error?.error?.message || 'Failed to record payment',
          type: TOAST_TYPES.ERROR,
        }),
      );
    }
  };

  return (
    <Box
      sx={{
        padding: '1rem',
        backgroundColor: '#fff',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography fontSize={'1rem'} fontWeight={700} color={'#6C6C6C'} lineHeight={'1.25rem'}>
          Payment
        </Typography>

        {isAdmin && isPaymentInitiated && (
          <Button
            variant="text"
            disableRipple
            disableFocusRipple
            disableTouchRipple
            disableElevation
            startIcon={<ReplayIcon sx={{ height: '14px' }} />}
            sx={{
              fontSize: '0.875rem',
              fontWeight: 700,
              lineHeight: '1.25rem',
              color: '#51B8B0',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            onClick={handleResetPayment}
          >
            Reset
          </Button>
        )}
      </Stack>
      <Stack
        gap={2}
        sx={{
          alignItems: isPaymentCompleted ? 'flex-start' : 'center',
        }}
      >
        {/* Payment Form */}
        {showPaymentForm && (
          <Box
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
            }}
          >
            <CustomDatetimepicker
              required
              label="Payout Date"
              error={(touched.paymentDate || submitted) && !!errors.paymentDate}
              errorMessage={
                (touched.paymentDate || submitted) && !!errors.paymentDate ? errors.paymentDate : ''
              }
              initialValue={form.paymentDate}
              handleValueChange={(value: any) => handleFormChange('paymentDate', value)}
              onBlur={() => handleBlurOnForm('paymentDate')}
            />

            <Box>
              <TextField
                required
                sx={{
                  width: '100%',
                }}
                label="Transaction ID"
                value={form.transactionId}
                onChange={(e) => handleFormChange('transactionId', e.target.value)}
                onBlur={() => handleBlurOnForm('transactionId')}
                error={(touched.transactionId || submitted) && !!errors.transactionId}
                helperText={
                  (touched.transactionId || submitted) && !!errors.transactionId
                    ? errors.transactionId
                    : ''
                }
              />
            </Box>

            <Box
              sx={{
                gridColumn: 'span 2',
              }}
            >
              <TextField
                sx={{
                  width: '100%',
                }}
                label="Description"
                value={form.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                onBlur={() => handleBlurOnForm('description')}
              />
            </Box>
          </Box>
        )}

        {!isPaymentCompleted && (
          <Box
            sx={{
              position: 'relative',
            }}
          >
            {/* Top Section */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
              }}
            >
              {isPaymentPending && (
                <Box
                  sx={{
                    height: '12px',
                    width: '12px',
                    borderRadius: '50%',
                    backgroundColor: INITIATED_GRAPHICS_COLOR,
                  }}
                ></Box>
              )}

              {isPaymentInitiated && <CheckIcon sx={{ color: INITIATED_GRAPHICS_COLOR }} />}

              <Box
                sx={{
                  width: '250px',
                  height: '2px',
                  backgroundColor: isPaymentPending
                    ? PENDING_GRAPHICS_COLOR
                    : INITIATED_GRAPHICS_COLOR,
                }}
              ></Box>

              <Box
                sx={{
                  height: '12px',
                  width: '12px',
                  borderRadius: '50%',
                  backgroundColor: isPaymentPending
                    ? PENDING_GRAPHICS_COLOR
                    : INITIATED_GRAPHICS_COLOR,
                }}
              ></Box>
            </Box>

            {/* Bottom Section */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                justifyContent: 'space-between',
                mt: '1rem',
              }}
            >
              <Box
                sx={{
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                {isAdmin && (
                  <Button
                    disableElevation
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    variant="contained"
                    disabled={!isPaymentPending || isUpdatingPayoutStatus}
                    sx={{
                      borderRadius: '1rem',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      lineHeight: '1.25rem',
                      color: '#fff',
                      backgroundColor: '#2E736E',
                      textTransform: 'none',
                    }}
                    onClick={handleApprovePayment}
                  >
                    {isUpdatingPayoutStatus && isPaymentPending ? 'Approving...' : 'Approve'}
                  </Button>
                )}

                {!isAdmin && (
                  <Typography
                    sx={{
                      ...paymentStatusStyle,
                      color: isPaymentPending ? INITIATED_TEXT_COLOR : PENDING_TEXT_COLOR,
                    }}
                  >
                    {isPaymentPending ? 'Payment Pending' : 'Payment Initiated'}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  transform: 'translateX(50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                {isAdmin && (
                  <Button
                    disableElevation
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    variant="contained"
                    disabled={!isPaymentInitiated || isUpdatingPayoutStatus}
                    onClick={handleRecordPayment}
                    sx={{
                      borderRadius: '1rem',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      lineHeight: '1.25rem',
                      color: '#fff',
                      backgroundColor: '#2E736E',
                      textTransform: 'none',
                    }}
                  >
                    {isUpdatingPayoutStatus && isPaymentInitiated
                      ? 'Recording...'
                      : 'Record Payment'}
                  </Button>
                )}

                {!isAdmin && (
                  <Typography
                    sx={{
                      ...paymentStatusStyle,
                      color: !isPaymentInitiated ? PENDING_TEXT_COLOR : INITIATED_TEXT_COLOR,
                    }}
                  >
                    Record Payment
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {isPaymentCompleted && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '1.5rem',
              width: '100%',
            }}
          >
            {isAdmin && (
              <Box>
                <Typography sx={labelStyle}>Approved Date</Typography>
                <Typography sx={textStyle}>
                  {approvedDate ? dayjs(approvedDate).format('DD MMM YYYY hh:mm A') : '-'}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography sx={labelStyle}>Payout Date</Typography>
              <Typography sx={textStyle}>
                {paymentDate ? dayjs(paymentDate).format('DD MMM YYYY hh:mm A') : '-'}
              </Typography>
            </Box>

            <Box sx={{ overflow: 'hidden' }}>
              <Typography sx={labelStyle}>Transaction ID</Typography>
              <Typography sx={textStyle}>{transactionId || '-'}</Typography>
            </Box>

            <Box sx={{ overflow: 'hidden' }}>
              <Typography sx={labelStyle}>Description</Typography>
              <Typography sx={textStyle}>{description || '-'}</Typography>
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default PaymentStatus;
